import { ZoningType, zoning_indice_sources } from '@popety-io/types'

/**
 * Zoning type priority order (highest to lowest)
 * Cantonal > Local > Partial > General
 */
export const ZONING_TYPE_PRIORITY: ZoningType[] = ['cantonal', 'local', 'partial', 'general']

/**
 * Tab labels for each zoning type
 */
export const ZONING_TYPE_LABELS: Record<ZoningType, string> = {
  cantonal: 'Cantonal',
  local: 'Local',
  partial: 'Partial',
  general: 'Communal', // General zones are labeled as "Communal" in the UI
}

/**
 * Colors for each zoning type
 * Used for active tab border, badge count (when > 0), and subtitle left border
 */
export const ZONING_TYPE_COLORS: Record<ZoningType, string> = {
  cantonal: '#e65100', // Warning orange — highest authority
  local: '#e65100', // Warning orange — detailed planning
  partial: '#d4a017', // Yellow — overlay/special restrictions
  general: '#0294df', // Popety blue — base zoning (default)
}

/**
 * Icons for each zoning type
 * Icons chosen to represent authority levels and planning hierarchy
 */
export const ZONING_TYPE_ICONS: Record<ZoningType, string> = {
  cantonal: 'shield', // Highest authority/enforcement
  local: 'home', // Neighborhood/district level detail
  partial: 'layers', // Overlay/special restrictions
  general: 'grid', // Base framework/classification
}

/**
 * Priority labels for each zoning type
 * Based on Swiss planning law hierarchy across all cantons
 */
export const ZONING_TYPE_PRIORITY_LABELS: Record<ZoningType, string> = {
  cantonal: 'Cantonal Authority - Overrides All',
  local: 'Detailed Planning - Neighborhood Rules',
  partial: 'Special Zones - Additional Restrictions',
  general: 'Base Zoning - Fundamental Land Use',
}

/**
 * Descriptions for each zoning type
 * Accurate descriptions reflecting Swiss planning law across French, German, and Italian cantons
 */
export const ZONING_TYPE_DESCRIPTIONS: Record<ZoningType, string> = {
  cantonal:
    'Cantonal zoning regulations (PAC, Kantonaler Nutzungsplan) set by regional authorities. These rules take absolute precedence over all municipal plans and define overarching development constraints such as protected areas, regional infrastructure corridors, and cantonal interests.',
  local:
    'Detailed neighborhood development plans (PQ, PLQ, Quartierplan, Gestaltungsplan) that specify precise construction requirements including building placement, dimensions, access, and design guidelines for specific districts within the municipality.',
  partial:
    'Special overlay zones (PPA, Sondernutzungsplan, IBUS zones) that apply additional restrictions or modifications on top of base zoning. These can include heritage protection, modified density ratios, environmental constraints, or specific development requirements.',
  general:
    'Municipal base zoning (PGA, PAL, BZO, Nutzungszonenplan) that establishes the fundamental land use category for this parcel, defining whether it is designated for residential, commercial, industrial, agricultural use, or is a non-building zone.',
}

/**
 * Color coding for zoning data sources
 * Indicates the confidence/reliability level of the data
 */
export const SOURCE_COLORS: Record<zoning_indice_sources, string> = {
  manual: '#00B341', // Green (high confidence - manually verified)
  geoportal: '#0294df', // Blue (official data from geoportal)
  llm: '#FF9500', // Orange (AI-generated, needs verification)
  estimate: '#FFD60A', // Yellow (estimated value)
  old: '#999999', // Gray (legacy data)
}

/**
 * Source labels for display
 */
export const SOURCE_LABELS: Record<zoning_indice_sources, string> = {
  manual: 'Manual',
  geoportal: 'Geoportal',
  llm: 'AI Generated',
  estimate: 'Estimated',
  old: 'Legacy',
}

/**
 * Metric field definitions with labels and units
 */
export interface MetricDefinition {
  label: string
  unit?: string
  category: 'ratio' | 'height' | 'floor' | 'setback' | 'usage' | 'environmental' | 'other'
  /** If true, value is stored as decimal (0.3) but should display as percentage (30%) */
  isPercentage?: boolean
}

export const METRIC_DEFINITIONS: Record<string, MetricDefinition> = {
  // Building indices (ratios)
  cos: { label: 'COS', unit: '', category: 'ratio' },
  cus: { label: 'CUS', unit: '', category: 'ratio' },
  ibus: { label: 'IBUS', unit: '', category: 'ratio' },
  ios: { label: 'IOS', unit: '', category: 'ratio' },
  ius: { label: 'IUS', unit: '', category: 'ratio' },
  im: { label: 'IM', unit: '', category: 'ratio' },
  vbr: { label: 'VBR', unit: '', category: 'ratio' },

  // Heights
  altitude: { label: 'Altitude', unit: 'm', category: 'height' },
  building_roof_height: { label: 'Roof Height', unit: 'm', category: 'height' },
  building_total_height: {
    label: 'Total Height',
    unit: 'm',
    category: 'height',
  },
  building_without_roof_height: {
    label: 'Height w/o Roof',
    unit: 'm',
    category: 'height',
  },

  // Floors
  floor_nb: { label: 'Total Floors', unit: 'floors', category: 'floor' },
  main_floor_nb: { label: 'Main Floors', unit: 'floors', category: 'floor' },
  attic_floor_nb: { label: 'Attic Floors', unit: 'floors', category: 'floor' },
  underground_floor_nb: {
    label: 'Underground Floors',
    unit: 'floors',
    category: 'floor',
  },

  // Usage ratios (stored as decimals, displayed as percentages)
  commercial_ratio: {
    label: 'Commercial Ratio',
    unit: '%',
    category: 'usage',
    isPercentage: true,
  },
  living_ratio: {
    label: 'Living Ratio',
    unit: '%',
    category: 'usage',
    isPercentage: true,
  },
  office_ratio: {
    label: 'Office Ratio',
    unit: '%',
    category: 'usage',
    isPercentage: true,
  },

  // Environmental metrics
  green_ratio: {
    label: 'Green Ratio',
    unit: '%',
    category: 'environmental',
    isPercentage: true,
  },
  noise_sensibility_level: {
    label: 'Noise Sensitivity Level',
    unit: '',
    category: 'environmental',
  },

  // Setbacks & dimensions
  big_setback: { label: 'Big Setback', unit: 'm', category: 'setback' },
  small_setback: { label: 'Small Setback', unit: 'm', category: 'setback' },
  distance_limit: { label: 'Distance Limit', unit: 'm', category: 'setback' },
  building_depth: { label: 'Building Depth', unit: 'm', category: 'setback' },
  length: { label: 'Max Length', unit: 'm', category: 'setback' },

  // Environmental metrics (continued)
  open_space_ratio: {
    label: 'Open Space Ratio',
    unit: '%',
    category: 'environmental',
    isPercentage: true,
  },

  // Other metrics
  density: { label: 'Density', unit: '', category: 'other' },
  unit_nb: { label: 'Number of Units', unit: 'units', category: 'other' },
  roof_slope: { label: 'Roof Slope', unit: '°', category: 'other' },
}

/**
 * Messages and labels
 */
export const MESSAGES = {
  LOADING: 'Loading zoning data...',
  ERROR: 'Failed to load zoning data',
  NO_DATA: 'No zoning data available for this land',
  NO_ZONES_FOR_TYPE: 'No zones found for this type',
  SECTION_DESCRIPTION: 'Zoning regulations and development potential for this land parcel',
}
